import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import { AgGridFilterType, FilterOption } from '@tassat/common-definitions';
import { WalletRecordMapped } from '@tassat/fred-extended-definitions';
import React from 'react';

import history from 'Lib/browserHistory';
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAllMappedWalletsByFirmId } from 'Selectors/accounts';
import BooleanCellRenderer from 'SharedComponents/cellRenderComponent/booleanCellRenderer';
import FormatAvailBalance from 'SharedComponents/cellRenderComponent/formatAvailBalance';
import FormatCurrBalance from 'SharedComponents/cellRenderComponent/formatCurrBalance';
import IsPrimaryCellRenderer from 'SharedComponents/cellRenderComponent/isPrimaryCellRenderer';
import LastTransactionDateCellRenderer from 'SharedComponents/cellRenderComponent/lastTransactionDateCellRenderer';
import MultiLineCellRenderer from 'SharedComponents/cellRenderComponent/multiLineCellRenderer';
import WalletNameCellRenderer from 'SharedComponents/cellRenderComponent/walletNameCellRenderer';
import WalletStatusCellRenderer from 'SharedComponents/cellRenderComponent/walletStatusCellRenderer';

import { firmWalletsColumnDefs } from './firmWalletsAgGridOptions';
import { FIRM_VIEW_WALLETS_TAB } from '../..';

import { FirmWalletsAgGridProps } from '.';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';

const closedWalletFilter: FilterOption = {
    title: 'Closed Accounts',
    colId: 'wallet.IsClosed',
    type: AgGridFilterType.Equal,
    filter: 'false'
};

export function FirmWalletsAgGrid (props: FirmWalletsAgGridProps) {
    const {
        firmId,
        showClosedWallets
    } = props;
    const wallets = useAppSelector((state) => getAllMappedWalletsByFirmId(state, firmId));

    const firmWalletSelected = (params: { data: WalletRecordMapped }) => {
        if (window.getSelection()
            .toString().length > 0) return;

        const wallet = params.data;
        history.push(`/admin/firms/${wallet.wallet.FirmId}/accounts/${wallet.wallet.AccountId}`, { tab: FIRM_VIEW_WALLETS_TAB });
    };

    const applyClosedFilter = (params, enable = false) => {
        const model = {
            filterType: 'text',
            type: 'equals',
            filter: enable ? 'true' : 'false'
        };
        params.api.setColumnFilterModel(closedWalletFilter.colId, model);
        params.api.onFilterChanged();
    };

    const [ gridApi, onGridReady ] = useAgGrid(applyClosedFilter);

    React.useEffect(() => {
        if (gridApi) {
            applyClosedFilter(gridApi, showClosedWallets);
        }
    }, [
        showClosedWallets,
        gridApi,
        wallets
    ]);

    return (
        <div
            className='ag-theme-material'
            id='firmWalletsGrid'
            style={{ height: '100%' }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={firmWalletsColumnDefs}
                columnMenu='legacy'
                components={{
                    lastTransactionDateCellRenderer: LastTransactionDateCellRenderer,
                    multiLineCellRenderer: MultiLineCellRenderer,
                    walletNameCellRenderer: WalletNameCellRenderer,
                    walletStatusCellRenderer: WalletStatusCellRenderer,
                    walletPermissionsCellRenderer: BooleanCellRenderer,
                    formatAvailBalance: FormatAvailBalance,
                    formatCurrBalance: FormatCurrBalance,
                    isPrimaryCellRenderer: IsPrimaryCellRenderer
                }}
                context={{ wallets: structuredClone(wallets) }}
                defaultColDef={{
                    resizable: true,
                    filter: true,
                    sortable: true
                }}
                enableCellTextSelection={true}
                getRowId={({ data }) => data?.wallet?.AccountId}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onGridReady={onGridReady}
                onRowClicked={firmWalletSelected}
                rowClassRules={{ 'lockedRow': (params: { data: WalletRecordMapped }) => params.data?.wallet?.IsLocked }}
                rowData={wallets}
                suppressCellFocus={true}
                suppressContextMenu={true}
            />
        </div>
    );
}